import React from 'react';

export interface MailPreviewModel {
  id: string;
  subject: string;
  to: string;
  html: string;
  canSend: boolean;
}

export function MailActionPills({
  noticeId,
  canSend,
  busy = false,
  sending = false,
  onPreview,
  onResend,
}: {
  noticeId: string;
  canSend: boolean;
  busy?: boolean;
  sending?: boolean;
  onPreview: () => void;
  onResend?: () => void;
}) {
  const disabled = busy || sending;
  return (
    <span className="job-history-pills">
      <button
        type="button"
        className="studio-secondary job-history-pill"
        data-testid={`preview-notice-${noticeId}`}
        disabled={disabled}
        onClick={onPreview}
      >
        {busy ? 'Opening…' : 'Preview'}
      </button>
      {canSend && onResend ? (
        <button
          type="button"
          className="studio-secondary job-history-pill"
          data-testid={`resend-notice-${noticeId}`}
          disabled={disabled}
          onClick={onResend}
        >
          {sending ? 'Sending…' : 'Resend'}
        </button>
      ) : null}
    </span>
  );
}

/** The preview is the sent HTML. Links stay visible and unclickable so the studio page cannot navigate away. */
export function quietMailLinks(html: string) {
  return html.replace('</body>', '<style>a{pointer-events:none;cursor:default}</style></body>');
}

const MailPreviewFrame = ({
  preview,
  note,
  sending = false,
  onSend,
  onClose,
}: {
  preview: MailPreviewModel;
  note?: string;
  sending?: boolean;
  onSend?: () => void;
  onClose: () => void;
}) => (
  <div className="studio-leave-backdrop" role="presentation">
    <div className="job-history-preview" role="dialog" aria-modal="true" aria-labelledby="history-mail-preview-title" data-testid="history-mail-preview">
      <h3 id="history-mail-preview-title">{preview.subject}</h3>
      <p>{preview.to ? `To ${preview.to}` : (preview.canSend ? 'To the client' : 'Email')}</p>
      <iframe title={preview.subject} sandbox="" srcDoc={quietMailLinks(preview.html)} />
      {note ? <p data-testid="history-mail-preview-note">{note}</p> : null}
      <div className="studio-toolbar">
        {preview.canSend && onSend ? (
          <button type="button" className="studio-primary" data-testid="history-mail-send" disabled={sending} onClick={onSend}>
            {sending ? 'Sending…' : 'Send to client'}
          </button>
        ) : null}
        <button type="button" className="studio-secondary" onClick={onClose} disabled={sending}>
          Close
        </button>
      </div>
    </div>
  </div>
);

export default MailPreviewFrame;
